import { makeStyles } from '@material-ui/styles';
import {
    useState,
    MutableRefObject,
    Dispatch,
    SetStateAction,
    ReactElement,
    SyntheticEvent,
} from 'react';
import Popper, { PopperPlacementType } from '@material-ui/core/Popper';
import cn from 'clsx';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Text from '../Text';
import { useTheme } from '@material-ui/core';
import { Theme } from '../../theme/types';
import Fade from './Fade';

type TooltipProps = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    anchorRef: MutableRefObject<HTMLElement | null>;
    text: ReactElement<string> | string | number;
    maxWidth?: number;
    placement?: PopperPlacementType;
    flip?: boolean;
    arrow?: boolean;
    customBackgroundColor?: string;
    customTextColor?: string;
    boundariesElement?: HTMLElement | string;
    mainElementProps?: { [key: string]: string | ((e: SyntheticEvent) => void) };
    clickAwayDisabled?: boolean;
    paperClassName?: string;
    arrowClassName?: string;
    tooltipWrapperClassName?: string;
    disablePortals?: boolean;
    disableSlideAnimation?: boolean;
};

const Tooltip: React.FC<TooltipProps> = ({
    children,
    open,
    setOpen,
    anchorRef,
    text,
    maxWidth,
    placement = 'top',
    flip = true,
    arrow = true,
    customBackgroundColor,
    customTextColor,
    boundariesElement,
    mainElementProps = {},
    clickAwayDisabled = false,
    paperClassName = '',
    arrowClassName = '',
    tooltipWrapperClassName = '',
    disablePortals = false,
    disableSlideAnimation = true,
}) => {
    const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);
    const theme = useTheme<Theme>();
    const backgroundColor = customBackgroundColor || theme.palette.greys[700];
    const textColor = customTextColor || theme.palette.greys[100];

    const classes = useStyles();

    const arrowClass = cn(classes.arrow, arrowClassName);

    const handleClickAway = () => {
        if (clickAwayDisabled) {
            return;
        }
        setOpen(false);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div className={cn(tooltipWrapperClassName)}>
                {children}
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement={placement as PopperPlacementType}
                    disablePortal={disablePortals}
                    className={classes.popper}
                    modifiers={{
                        flip: {
                            enabled: flip,
                        },
                        arrow: {
                            enabled: arrow,
                            element: arrowRef,
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: boundariesElement || 'disabled',
                        },
                    }}
                    transition
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} disableSlideAnimation={disableSlideAnimation}>
                            <div {...mainElementProps}>
                                {arrow ? (
                                    <div className={arrowClass} ref={setArrowRef}>
                                        <svg
                                            width="53"
                                            height="9"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                        >
                                            <path
                                                d="m0,0l53,0l-7.8481,0.00005c-6.107,0.00004 -11.9344,2.55937 -16.0664,7.05616l-0.1719,0.18709c-1.4687,1.59835 -4.0034,1.55618 -5.4181,-0.09013c-3.902,-4.5408 -9.591,-7.15308 -15.57807,-7.15312l-7.91743,-0.00005z"
                                                fill={backgroundColor}
                                            />
                                        </svg>
                                    </div>
                                ) : null}
                                <div
                                    style={{ backgroundColor, maxWidth: maxWidth || 225 }}
                                    className={cn(classes.paper, paperClassName)}
                                >
                                    <Text
                                        variant="t81"
                                        style={{ color: textColor }}
                                        className={classes.text}
                                    >
                                        {text}
                                    </Text>
                                </div>
                            </div>
                        </Fade>
                    )}
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

const useStyles = makeStyles(
    () => ({
        paper: {
            overflow: 'auto',
            borderRadius: 8,
            boxShadow: 'none',
            transition: 'opacity .5s',
            opacity: 1,
            padding: 7,
            width: 'fit-content',
        },
        popper: {
            zIndex: 10002,
            '&[x-out-of-boundaries]': {
                opacity: 0,
            },
            '&[x-placement*="bottom"]': {
                top: '12px !important',
            },
            '&[x-placement*="bottom"] $arrow': {
                top: 0,
                left: 0,
                marginTop: '-0.9em',
                width: 53,
                height: '1em',
                transform: 'rotate(180deg)',
            },
            '&[x-placement*="top"]': {
                top: '-12px !important',
            },
            '&[x-placement*="top"] $arrow': {
                bottom: 0,
                left: 0,
                marginBottom: '-0.9em',
                width: 53,
                height: '1em',
            },
            '&[x-placement*="right"]': {
                left: '12px !important',
            },
            '&[x-placement*="right"] $arrow': {
                left: -16,
                marginLeft: '-0.9em',
                height: '1em',
                width: 53,
                transform: 'rotate(90deg)',
            },
            '&[x-placement*="left"]': {
                left: '-12px !important',
            },
            '&[x-placement*="left"] $arrow': {
                right: -16,
                marginRight: '-0.9em',
                height: '1em',
                width: 53,
                transform: 'rotate(270deg)',
            },
        },
        arrow: {
            display: 'inline-flex',
            fontSize: 20,
            position: 'absolute',
        },
        text: {
            fontSize: 12,
            display: 'block',
        },
    }),
    { classNamePrefix: 'tooltip' }
);

export default Tooltip;
